import React, { useState, useRef, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';

import { isParisRegion } from 'utils/zones/deliveryZone';
import { useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import styled from 'styled-components';
import TextareaAutosize from 'react-autosize-textarea';

import { TotemLabel, TotemInput } from 'styles';
import {
    SettingsAutocompleteContainer,
    SettingsAutocompleteItem,
    SettingsAutocompleteItems,
} from 'styles/pages/settings.styles';

import { TotemButton } from 'ui/Button';

import { throwMessage } from 'utils/LoggingService';
import { extractKeysFromObject, objectChangeCheck } from 'utils';
import { isEveryRequiredFieldFilled } from 'utils/forms';

import { analyticsEvents, analyticsPropertiesConstants, analyticsSourceConstants, track } from 'tracking/segment';
import { InlineError } from 'ui/InlineError';
import { useGetPlaceAutocompleteSuggestionsLazyQuery, useGetPlaceDetailsLazyQuery } from 'data/__generated__';

const addressFields: string[] = ['address', 'zipcode', 'city', 'country', 'latitude', 'longitude'];

function formatAddressToShortString(addressDetails: { address: string; city: string; country: string }): string {
    return `${addressDetails.address}, ${addressDetails.city}, ${addressDetails.country}`;
}

interface AutocompleteSuggestion {
    reference?: string;
    label: string;
}

interface DeliveryInfoFormProps {
    buttonFontSize?: string | number;
    currentTotem: any;
    deliveryInfo: any;
    hasDeliveryInstructions?: boolean;
    hasLocationRestriction?: boolean;
    isAddressNonEditable?: boolean;
    isCreatingAddress?: boolean;
    loading?: boolean;
    onFormSubmitted?: () => void;
    requestDirty?: boolean;
    setDeliveryInfo: (info: any) => void;
    sendFormContent: (info: any) => void;
    submitLabel?: string;
    source?: string;
}

export const DeliveryInfoForm: React.FC<DeliveryInfoFormProps> = ({
    buttonFontSize,
    currentTotem,
    deliveryInfo,
    hasDeliveryInstructions = true,
    hasLocationRestriction = true,
    isAddressNonEditable,
    isCreatingAddress,
    loading,
    onFormSubmitted = () => undefined,
    requestDirty = false,
    setDeliveryInfo,
    sendFormContent,
    submitLabel = 'Valider',
    source = analyticsSourceConstants.DASHBOARD,
}) => {
    const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<AutocompleteSuggestion[]>([]);
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const [isButtonEnabled, setButtonEnabled] = useState(true);
    const {
        setError,
        clearErrors,
        formState: { errors },
    } = useForm({
        reValidateMode: 'onSubmit',
    });

    const inputRef = useRef<HTMLInputElement>(null);

    const formFields: string[] = [...addressFields, 'address_details', 'consigneDelivery'];

    const [getAutocompleteSuggestions] = useGetPlaceAutocompleteSuggestionsLazyQuery({
        onCompleted: (data) => {
            const suggestions = data.getPlaceAutocompleteSuggestions.map((suggestion) => ({
                label: suggestion.description,
                reference: suggestion.placeId,
            }));
            setAutocompleteSuggestions(suggestions);
        },
    });

    const [getPlaceDetails] = useGetPlaceDetailsLazyQuery();

    const debouncedGetAutocompleteSuggestions = useMemo(
        () =>
            debounce((value: string) => {
                getAutocompleteSuggestions({
                    variables: {
                        input: value,
                    },
                });
            }, 1000),
        [getAutocompleteSuggestions],
    );

    useEffect(() => {
        const requiredFields = hasDeliveryInstructions ? ['address', 'address_details'] : ['address'];
        const fieldsFilled = isEveryRequiredFieldFilled(deliveryInfo, ...requiredFields);
        if (
            fieldsFilled /* the required fields have been filled */ &&
            deliveryInfo.latitude /* the address is well defined with GPS coordinates */ &&
            Object.keys(errors).length === 0 /* form has 0 errors */
        ) {
            if (!isButtonEnabled) {
                setButtonEnabled(true);
            }
        } else if (isButtonEnabled) {
            setButtonEnabled(false);
        }
    }, [deliveryInfo, isButtonEnabled, errors, errors.address, hasDeliveryInstructions]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const fieldsHaveChanged = objectChangeCheck(currentTotem, deliveryInfo, formFields as any);
        if (fieldsHaveChanged) {
            // check address is in Paris
            clearErrors('address');
            if (!isCreatingAddress) {
                track(analyticsEvents.MODIFY_DELIVERY_INFO, {
                    [analyticsPropertiesConstants.COMMON.SOURCE]: source,
                    [analyticsPropertiesConstants.ORDER.DELIVERY.CITY]: deliveryInfo?.city,
                    [analyticsPropertiesConstants.ORDER.DELIVERY.POSTAL_CODE]: deliveryInfo?.zipcode,
                    [analyticsPropertiesConstants.ORDER.DELIVERY.IS_OUTSIDE_DELIVERY_ZONE]: !isParisRegion(
                        deliveryInfo.zipcode,
                    ),
                });
            }
            if (hasLocationRestriction && !isParisRegion(deliveryInfo.zipcode)) {
                const message = "Cette adresse n'est pas encore dans notre périmètre de livraison.";
                setError('address', { type: 'typeError', message });
                return;
            }
            const newDeliveryInfo = extractKeysFromObject(deliveryInfo, formFields);
            await sendFormContent(newDeliveryInfo);
        }
        onFormSubmitted();
    };

    const handleAddressDetailsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setDeliveryInfo({ ...deliveryInfo, [name]: value });
    };

    const handleAddressSelection = (selectedSuggestion: AutocompleteSuggestion) => async () => {
        setAutocompleteOpen(false);

        if (!selectedSuggestion?.reference) {
            throwMessage('error', 'No place reference found');
            return;
        }

        try {
            const { data } = await getPlaceDetails({
                variables: { placeId: selectedSuggestion.reference },
            });

            if (!data?.getPlaceDetails) {
                throwMessage('error', 'Failed to get place details');
                return;
            }

            const detail = data.getPlaceDetails;

            if (!detail.streetNumber) {
                const message = 'Veuillez choisir une adresse complète';
                setError('address', { type: 'locationError', message });
                return;
            }

            if (!detail.city || !detail.country || !detail.zipcode) {
                throwMessage('error', 'An error occurred while saving the address');
                return;
            }

            const selectionAddressDetails = {
                address: detail.name || '',
                latitude: detail.latitude,
                longitude: detail.longitude,
                city: detail.city,
                zipcode: detail.zipcode,
                country: detail.country,
            };

            if (inputRef.current) {
                inputRef.current.value = formatAddressToShortString(selectionAddressDetails);
            }

            setDeliveryInfo({ ...deliveryInfo, ...selectionAddressDetails });
        } catch {
            throwMessage('error', 'Failed to get place details');
        }
    };

    const handleAddressAutocompleteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value) {
            debouncedGetAutocompleteSuggestions(value);
            clearErrors('address');
            setAutocompleteOpen(true);
        } else {
            debouncedGetAutocompleteSuggestions.cancel();
            setAutocompleteOpen(false);
        }
    };

    const handleBlur = () => {
        setAutocompleteOpen(false);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value) {
            setAutocompleteOpen(true);
        }
    };
    const valueProp = isAddressNonEditable
        ? {
              value: formatAddressToShortString(deliveryInfo),
          }
        : {
              defaultValue:
                  deliveryInfo.address && deliveryInfo.zipcode && deliveryInfo.city
                      ? formatAddressToShortString(deliveryInfo)
                      : '',
          };
    // Ensure $fontSize is always a string for TotemButton
    const buttonFontSizeString = typeof buttonFontSize === 'number' ? `${buttonFontSize}px` : buttonFontSize;

    return (
        <FormContainer onSubmit={handleSubmit}>
            <TotemLabel>
                Adresse
                <TotemInput
                    {...valueProp}
                    readOnly={isAddressNonEditable}
                    ref={inputRef}
                    required
                    placeholder="Entrez votre adresse de livraison"
                    name="address"
                    onChange={handleAddressAutocompleteChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    $={[0.25, 0, 1, 0]}
                    data-test="deliveries-address"
                />
                {errors['address'] && <InlineError display={errors.address} />}
            </TotemLabel>
            {autocompleteOpen && autocompleteSuggestions.length > 0 && (
                <SettingsAutocompleteContainer>
                    <SettingsAutocompleteItems>
                        {autocompleteSuggestions.map((suggestion, index) => (
                            <SettingsAutocompleteItem key={index} onMouseDown={handleAddressSelection(suggestion)}>
                                {suggestion.label}
                            </SettingsAutocompleteItem>
                        ))}
                    </SettingsAutocompleteItems>
                </SettingsAutocompleteContainer>
            )}
            {hasDeliveryInstructions && (
                <>
                    <TotemLabel>
                        Étage, code, bâtiment et autres instructions d'accès à votre TOTEM
                        <TotemInput
                            async // necessary because we use styled-component
                            {...({} as any)}
                            as={TextareaAutosize}
                            required
                            rows={3}
                            placeholder="Entrez les instructions pour permettre au livreur d'accéder à vos locaux"
                            name="address_details"
                            onChange={handleAddressDetailsChange}
                            value={deliveryInfo.address_details}
                            data-test="deliveries-details"
                        />
                    </TotemLabel>
                    <TotemLabel>
                        Consignes de mise en place
                        <TotemInput
                            async // necessary because we use styled-component
                            {...({} as any)}
                            as={TextareaAutosize}
                            rows={3}
                            placeholder="Entrez vos consignes de mise en place"
                            name="consigneDelivery"
                            onChange={handleAddressDetailsChange}
                            value={deliveryInfo.consigneDelivery}
                            data-test="deliveries-instructions"
                            maxHeight="300px"
                        />
                    </TotemLabel>
                </>
            )}
            <TotemButton
                $margins={[1, 0, 2, 0]}
                data-test="address-modify-button"
                disabled={
                    !isButtonEnabled ||
                    (requestDirty && !objectChangeCheck(currentTotem, deliveryInfo, formFields as any))
                }
                $fontSize={buttonFontSizeString}
                type="submit"
            >
                {loading ? <ClipLoader color="white" size="16px" /> : submitLabel}
            </TotemButton>
        </FormContainer>
    );
};

const FormContainer = styled.form`
    display: flex;
    width: 100%;
    flex-direction: column;
`;
